import { ThumbnailInfo } from '@app/core/hooks/api/useApiThumbnailsRange';
import { useEffect, useRef } from 'react';
import { useMeasureDirty } from '../../../hooks/useMeasureDirty';
import { useThumbnailLayout } from './useThumbnailLayout';

interface Props {
  thumbHeight: number;
  thumbnails: ThumbnailInfo[];
  streamEl: HTMLDivElement;
}

/**
 * Optimized ThumbnailsRoll component that uses CSS background positioning
 * to show only the first frame from each sprite sheet, instead of loading
 * the entire sprite sheet.
 *
 * Key optimizations:
 * 1. Uses CSS background-position to display only the first frame (offset 0) from each sprite
 * 2. Memoizes calculations to avoid recalculating on every render
 * 3. Uses foreignObject to embed HTML with CSS in SVG for better performance
 */
export function ThumbnailsRoll({ thumbHeight, thumbnails, streamEl }: Props) {
  // Create a ref that points to streamEl for useMeasureDirty
  const streamElRef = useRef(streamEl);
  useEffect(() => {
    streamElRef.current = streamEl;
  }, [streamEl]);

  // Track container width using useMeasureDirty hook
  const { width: containerWidth } = useMeasureDirty(streamElRef);

  // Calculate thumbnail layouts by tick count (distribute evenly)
  const thumbnailLayouts = useThumbnailLayout({
    thumbnails,
    streamEl,
    thumbHeight,
    containerWidth,
  });

  if (!containerWidth) {
    return null;
  }

  return (
    <>
      {thumbnailLayouts.map(({ thumbnail, width, x, spriteWidth }, index) => (
        <svg key={`${thumbnail.thumbnailUrl}-${index}`} width={width} height={thumbHeight} x={x}>
          <foreignObject width={width} height={thumbHeight} x={0} y={0}>
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${thumbnail.thumbnailUrl})`,
                backgroundSize: `${spriteWidth}px auto`,
                backgroundPosition: '0px 0px',
                backgroundRepeat: 'no-repeat',
                imageRendering: 'auto',
              }}
            />
          </foreignObject>
        </svg>
      ))}
    </>
  );
}
